import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { getEventSemantics } from "features/VehicleSidebar/AffectedTrains/utils/utils";
import {
  ButtonGroup,
  InvisibleButton,
} from "features/VehicleSidebar/VehicleList/TrainList/styles";
import { TrainData } from "features/VehicleSidebar/VehicleList/TrainList/types";
import { internalMessagesHasTrain } from "features/VehicleSidebar/VehicleList/TrainList/utils";
import { useTranslation } from "react-i18next";
import { CallButton } from "shared/components/buttons/CallButton";
import { DropsBadge, Flex, DropsTooltip, VStack } from "shared/components/Spor";
import { NoteOutline18Icon } from "shared/icons/NoteIcon";

const getSemanticsForEvents = (types: string[]) => {
  if (types.some((type) => getEventSemantics(type) === "alarm")) {
    return "alarm";
  }
  if (types.some((type) => getEventSemantics(type) === "warning")) {
    return "warning";
  }
  return "info";
};

type trainEventTitlesSchema = {
  type: string;
  label: string;
  priority: number;
};

const trimType = (label: string) => {
  const trimmed = label.split(" (")[0];
  return trimmed.length > 15 ? `${trimmed.substring(0, 12)}...` : trimmed;
};

export const RightContent = ({
  train,
  eventNames: [firstEvent, ...otherEvents],
  internalMessages,
}: {
  train: TrainData;
  eventNames: trainEventTitlesSchema[];
  internalMessages: InternalMessageResponse[] | undefined;
}) => {
  const { t } = useTranslation();

  const eventTypes = otherEvents.map((event) => event.type).filter(Boolean);

  const hasInternalMessage = internalMessagesHasTrain(internalMessages, train);

  return (
    <ButtonGroup role="group">
      <VStack gap="12px" alignItems="flex-end">
        <Flex wrap="wrap" justifyContent="flex-end" gap="3px">
          {firstEvent && (
            <DropsBadge semantics={getSemanticsForEvents([firstEvent.type])}>
              {trimType(firstEvent.label)}
            </DropsBadge>
          )}

          {otherEvents.length > 0 && (
            <DropsTooltip
              hasArrow
              placement="right"
              label={
                <VStack>
                  {otherEvents.map((event) => (
                    <DropsBadge
                      key={event.label}
                      semantics={getSemanticsForEvents([event.type])}
                    >
                      {trimType(event.label)}
                    </DropsBadge>
                  ))}
                </VStack>
              }
            >
              <InvisibleButton>
                <DropsBadge semantics={getSemanticsForEvents(eventTypes)}>
                  +{otherEvents.length}
                </DropsBadge>
              </InvisibleButton>
            </DropsTooltip>
          )}
          {hasInternalMessage && (
            <DropsTooltip
              hasArrow
              placement="right"
              label={
                <DropsBadge semantics="neutral">
                  {t("Intern melding")}
                </DropsBadge>
              }
            >
              <DropsBadge px="3px !important" semantics="neutral">
                <NoteOutline18Icon transform="scale(0.75, 0.75)" />
              </DropsBadge>
            </DropsTooltip>
          )}
        </Flex>
        <Flex wrap="wrap" justifyContent="flex-end" gap="3px">
          {train.trainStaffOnDuty?.map((staffOnDuty) => (
            <CallButton
              key={`${staffOnDuty.gsmrNumber}_${staffOnDuty.functionalTelephoneNumber}_${staffOnDuty.function}`}
              label={staffOnDuty.function}
              phoneNumber={staffOnDuty.functionalTelephoneNumber}
              size="xs"
            />
          ))}
        </Flex>
      </VStack>
    </ButtonGroup>
  );
};
